import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, FileStack, BookMarked, Share2, Users, BarChart3 } from 'lucide-react'
import DashboardSidebar from '../components/DashboardSidebar'
import InheritCurriculumModal from '../components/InheritCurriculumModal'
import ShareModal from '../components/ShareModal'
import ResultsModal from '../components/ResultsModal'
import { lessonApi } from '../api/lessonBuilderApi'
import { countCards } from '../lib/sections'
import type { LessonDoc } from '../types/LessonDoc'

// Trang hub: danh sách bài giảng của giáo viên (dashboard e-learning).
const MyLessonsPage: React.FC = () => {
  const [lessons, setLessons] = useState<LessonDoc[]>([])
  const [shared, setShared] = useState<LessonDoc[]>([])
  const [showInherit, setShowInherit] = useState(false)
  const [shareTarget, setShareTarget] = useState<LessonDoc | null>(null)
  const [resultsTarget, setResultsTarget] = useState<LessonDoc | null>(null)
  const navigate = useNavigate()

  const refresh = () => {
    lessonApi.list().then(setLessons)
    lessonApi.sharedWithMe().then(setShared)
  }
  useEffect(refresh, [])

  const handleDelete = async (id: string) => {
    if (!confirm('Xoá bài giảng này?')) return
    try {
      await lessonApi.remove(id)
    } catch (e) {
      alert('Xoá thất bại trên máy chủ: ' + ((e as Error)?.message || e) + '\nBài có thể vẫn còn. Thử lại hoặc kiểm tra đăng nhập.')
    }
    refresh()
  }

  return (
    <div className="flex bg-[#F8FAFC]">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Bài giảng của tôi</h1>
            <p className="text-sm text-gray-500">Tạo, chỉnh sửa và xuất bản bài giảng e-learning</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInherit(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-50 text-sky-700 text-sm font-bold hover:bg-sky-100"
            >
              <BookMarked size={18} /> Kế thừa từ chương trình
            </button>
            <Link
              to="/soan-bai"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
            >
              <Plus size={18} /> Tạo bài giảng
            </Link>
          </div>
        </div>

        {lessons.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-300">
            <FileStack size={48} className="mb-3" />
            <p className="text-gray-400 font-medium">Chưa có bài giảng nào. Bấm "Tạo bài giảng" để bắt đầu.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lessons.map((l) => (
              <div key={l.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                      l.status === 'published' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                    }`}
                  >
                    {l.status === 'published' ? 'Đã xuất bản' : 'Bản nháp'}
                  </span>
                  <span className="text-[11px] text-gray-400">{countCards(l)} thẻ</span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{l.meta.tieuDe || 'Chưa đặt tên'}</h3>
                <p className="text-xs text-gray-400 mb-4 line-clamp-2 flex-1">{l.meta.moTa || '—'}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/soan-bai/${l.id}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 text-gray-700 text-sm font-semibold hover:bg-gray-100"
                  >
                    <Pencil size={15} /> Sửa
                  </button>
                  {l.status === 'published' && (
                    <button
                      onClick={() => navigate(`/bai-giang/${l.slug}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                    >
                      <Eye size={15} /> Xem
                    </button>
                  )}
                  <button
                    onClick={() => setResultsTarget(l)}
                    title="Kết quả học tập"
                    className="px-3 py-2 rounded-lg text-gray-400 hover:bg-emerald-50 hover:text-emerald-600"
                  >
                    <BarChart3 size={15} />
                  </button>
                  <button
                    onClick={() => setShareTarget(l)}
                    title="Chia sẻ"
                    className="px-3 py-2 rounded-lg text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Share2 size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(l.id)}
                    className="px-3 py-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bài giảng được chia sẻ với tôi */}
        {shared.length > 0 && (
          <div className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
              <Users size={18} className="text-blue-600" /> Được chia sẻ với tôi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shared.map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/bai-giang/${l.slug}`)}
                  className="bg-white rounded-2xl border border-blue-100 shadow-sm p-5 text-left hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                    Chia sẻ
                  </span>
                  <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{l.meta.tieuDe}</h3>
                  <p className="text-xs text-gray-400 line-clamp-2">{l.meta.moTa || '—'}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {showInherit && (
        <InheritCurriculumModal
          onClose={() => setShowInherit(false)}
          onCreated={(doc) => {
            setShowInherit(false)
            navigate(`/soan-bai/${doc.id}`)
          }}
        />
      )}

      {shareTarget && (
        <ShareModal
          resourceType="lesson"
          resourceId={shareTarget.id}
          resourceTitle={shareTarget.meta.tieuDe}
          onClose={() => setShareTarget(null)}
        />
      )}

      {resultsTarget && (
        <ResultsModal
          lessonId={resultsTarget.id}
          lessonTitle={resultsTarget.meta.tieuDe}
          onClose={() => setResultsTarget(null)}
        />
      )}
    </div>
  )
}

export default MyLessonsPage
